import React, { useState, useRef, useEffect } from 'react';
import Button from './UI/Button';
import Card from './UI/Card';
import GenericLimitDetail from './LimitSetup/GenericLimitDetail';
import HelpDetail from './LimitSetup/HelpDetail';
import { useTranslation } from '../hooks/useTranslation';
import { useSimulator } from '../context/SimulatorContext';

const checklist = [
  { 
    key: 'netDeposit', 
    labelKey: 'limits.net_deposit',
    descriptionKey: 'limits.descriptions.net_deposit'
  },
  { 
    key: 'deposit', 
    labelKey: 'limits.deposit',
    descriptionKey: 'limits.descriptions.deposit'
  },
  { 
    key: 'login', 
    labelKey: 'limits.login_time',
    descriptionKey: 'limits.descriptions.login'
  },
  { 
    key: 'withdrawal', 
    labelKey: 'limits.withdrawal',
    descriptionKey: 'limits.descriptions.withdrawal'
  },
];

export default function LimitSetup() {
  const { t } = useTranslation();
  const { actions, state } = useSimulator();
  
  const [completed, setCompleted] = useState({
    netDeposit: false,
    deposit: false,
    login: false,
    withdrawal: false,
  });
  const [activeDetail, setActiveDetail] = useState(null);
  const [lastCompleted, setLastCompleted] = useState(null);
  
  // Refs for focus management
  const checklistRefs = useRef({});
  const mainHeadingRef = useRef(null);

  const allComplete = Object.values(completed).every(Boolean);
  const completedCount = Object.values(completed).filter(Boolean).length;

  // Function to format limit values for display
  const formatLimitValues = (limitKey) => {
    const limits = state.limits;
    
    switch (limitKey) {
      case 'netDeposit':
        return `€${limits.netDeposit}`;
      case 'deposit':
        return `€${limits.deposit.daily} / €${limits.deposit.weekly} / €${limits.deposit.monthly}`;
      case 'login':
        return `${limits.login.daily}h / ${limits.login.weekly}h / ${limits.login.monthly}h`;
      case 'withdrawal':
        return `€${limits.withdrawal.maxBalance} (${t('common.reserve')}: €${limits.withdrawal.reserve})`;
      default:
        return '';
    }
  };

  // Handlers for saving each limit
  const handleSave = (key, values = null) => {
    console.log('LimitSetup: Saving limit', key, 'with values:', values);
    setCompleted(c => ({ ...c, [key]: true }));
    setActiveDetail(null);
    setLastCompleted(key);
    
    // Save the actual limit values to the simulator context
    if (values) {
      console.log('LimitSetup: Calling actions.setLimits with:', values);
      actions.setLimits(values);
    }
  };
  const handleBack = () => setActiveDetail(null);

  // Handle starting the simulation
  const handleStart = () => {
    actions.setLimitsConfigured(true); // Mark limits as configured
    actions.setLimitSetup(false); // Hide the limit setup screen
    actions.reset({ withNewLimits: false }); // Reset simulation with current limits
  };

  // Focus management when returning from detail views
  useEffect(() => {
    if (activeDetail === null && lastCompleted) {
      // Focus the next incomplete item or the main heading if all complete
      const nextIncomplete = checklist.find(item => !completed[item.key]);
      if (nextIncomplete && checklistRefs.current[nextIncomplete.key]) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          checklistRefs.current[nextIncomplete.key]?.focus();
        }, 100);
      } else if (allComplete && mainHeadingRef.current) {
        // If all complete, focus the main heading
        setTimeout(() => {
          mainHeadingRef.current?.focus();
        }, 100);
      }
      setLastCompleted(null);
    }
  }, [activeDetail, lastCompleted, completed, allComplete]);

  // Render detail view if one is active
  if (activeDetail && activeDetail !== 'help') {
    return (
      <GenericLimitDetail 
        limitType={activeDetail}
        onSave={handleSave}
        onBack={handleBack}
      />
    );
  }
  if (activeDetail === 'help') {
    return <HelpDetail onBack={handleBack} />;
  }

  // Main checklist view
  return (
    <Card className="max-w-2xl mx-auto">
      <div className="text-center mb-6">
        <h2 ref={mainHeadingRef} tabIndex="-1" className="text-2xl font-bold mb-2 text-heading text-hc-black">{t('app.title')}</h2>
        <p className="text-gray-600 text-body">
          {t('limits.setup.title')}
        </p>
      </div>

      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex justify-between text-sm text-gray-600 mb-2">
          <span>{t('limits.setup.progress')}</span>
          <span>{t('limits.setup.progress_count', { completed: completedCount, total: checklist.length })}</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="h-full bg-primary transition-all duration-300 ease-out"
            style={{ width: `${(completedCount / checklist.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Limit Cards */}
      <div className="grid gap-4 mb-8">
        {checklist.map(item => (
          <button
            key={item.key}
            ref={el => checklistRefs.current[item.key] = el}
            onClick={() => setActiveDetail(item.key)}
            aria-label={`${t(item.labelKey)} - ${completed[item.key] ? t('common.completed') : t('common.click_to_set')}`}
            aria-describedby={`${item.key}-description`}
            className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-lg ${
              completed[item.key]
                ? 'border-hc-black/50'
                : 'border-gray-200 hover:border-primary/50'
            }`}
          >
            <div className="flex items-start gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-semibold text-medium">{t(item.labelKey)}</h3>
                  <span className={`text-sm font-medium ${
                    completed[item.key] ? 'text-hc-black/50' : 'text-gray-400'
                  }`}>
                    {completed[item.key] ? (
                      <span className="flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {t('common.completed')}
                      </span>
                    ) : (
                      t('common.click_to_set')
                    )}
                  </span>
                </div>
                <p id={`${item.key}-description`} className="text-sm text-gray-600 text-body">{t(item.descriptionKey)}</p>
                {completed[item.key] && (
                  <div className="mt-2 text-sm font-medium text-hc-black/50">
                    {formatLimitValues(item.key)}
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <Button
          variant="secondary"
          fullWidth
          onClick={() => setActiveDetail('help')}
          className="flex items-center justify-center gap-2"
        >
          <span>{t('limits.setup.help_button')}</span>
        </Button>
        <Button
          variant="primary"
          fullWidth
          disabled={!allComplete}
          onClick={handleStart}
          className="flex items-center justify-center gap-2"
        >
          <span>{t('simulation.start')}</span>
        </Button>
      </div>
    </Card>
  );
} 